import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingCart, BarChart3, Globe, Wallet, Menu,
  Package, Truck, Settings,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * Bottom navigation bar — visible uniquement sur mobile (< lg).
 * Affiche jusqu'à 4 raccourcis adaptés au rôle + un bouton « Plus » qui ouvre
 * la sidebar complète.
 *
 * ⚠️ CE COMPOSANT EST CRITIQUE : sur mobile, le bouton « Plus » ci-dessous est
 * le SEUL moyen d'ouvrir le menu (cf. Layout.js — il n'y a pas de hamburger
 * dans l'en-tête). Si cette barre ne s'affiche pas, l'utilisateur se retrouve
 * bloqué sur le tableau de bord, sans aucune navigation.
 *
 * C'est exactement ce qui est arrivé : la migration 023 a renommé le rôle
 * 'employe' en 'gerant_vendeur', mais ce fichier était resté sur l'ancien nom.
 * NAV_BY_ROLE['gerant_vendeur'] valait undefined → items vide → return null →
 * plus de barre → plus de menu. Idem pour le rôle 'consultation' (migration 063),
 * qui n'avait jamais été ajouté ici.
 *
 * D'où la règle ci-dessous : on ne renvoie JAMAIS null. Un rôle inconnu garde
 * au minimum le bouton « Plus » et reste donc capable de naviguer.
 *
 * Rôles (voir AuthContext + migrations 023 / 063) :
 *   - admin           : supervision + catalogue + réglages (pas le métier quotidien)
 *   - proprietaire    : tout
 *   - gerant_vendeur  : le métier de sa boutique (ex-'employe')
 *   - consultation    : tout le métier, en lecture seule
 *   - vendeur         : aucun accès à l'interface web (login bloqué)
 */

// moduleCode : même clé que la Sidebar, pour masquer les entrées dont le
// module n'est pas activé pour ce commerce (lot L07).
const NAV_GERANT = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau' },
  { to: '/ventes',    icon: ShoppingCart,    label: 'Ventes',   moduleCode: 'ventes_magasin' },
  { to: '/stock',     icon: BarChart3,       label: 'Stock',    moduleCode: 'stock' },
  { to: '/commandes', icon: Globe,           label: 'En ligne', moduleCode: 'commandes_ligne' },
];

const NAV_BY_ROLE = {
  admin: [
    { to: '/dashboard',    icon: LayoutDashboard, label: 'Tableau' },
    { to: '/produits',     icon: Package,         label: 'Produits',     moduleCode: 'produits' },
    { to: '/fournisseurs', icon: Truck,           label: 'Fournisseurs', moduleCode: 'fournisseurs' },
    { to: '/parametres',   icon: Settings,        label: 'Réglages' },
  ],
  proprietaire: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau' },
    { to: '/ventes',    icon: ShoppingCart,    label: 'Ventes',   moduleCode: 'ventes_magasin' },
    { to: '/caisse',    icon: Wallet,          label: 'Caisse',   moduleCode: 'caisse' },
    { to: '/commandes', icon: Globe,           label: 'En ligne', moduleCode: 'commandes_ligne' },
  ],
  gerant_vendeur: NAV_GERANT,
  // Filet de sécurité : si un compte a échappé au renommage de la migration 023.
  employe: NAV_GERANT,
  // Lecture seule : mêmes pages métier, la restriction d'écriture est gérée
  // côté serveur (middleware role-consultation) et dans chaque page.
  consultation: NAV_GERANT,
};

export default function BottomNav({ onOpenMenu }) {
  const { user, moduleActif } = useAuth();

  const items = (NAV_BY_ROLE[user?.role] || [])
    .filter(item => moduleActif(item.moduleCode));

  // ⚠️ NE JAMAIS renvoyer null ici. Voir le commentaire en tête de fichier :
  // sans cette barre, il n'existe aucun moyen d'ouvrir le menu sur mobile.
  // Un rôle inconnu doit au minimum conserver le bouton « Plus ».

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-gray-900 border-t border-gray-800 shadow-2xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-label="Navigation principale"
    >
      <ul className="flex items-stretch justify-around">
        {items.map(({ to, icon: Icon, label }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 px-1 min-h-[60px] text-[11px] font-medium transition-colors
                ${isActive
                  ? 'text-yellow-400'
                  : 'text-gray-400 hover:text-gray-200 active:text-yellow-300'
                }`
              }
              aria-label={label}
            >
              {({ isActive }) => (
                <>
                  <Icon size={22} strokeWidth={isActive ? 2.4 : 2} />
                  <span className="leading-none">{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
        <li className="flex-1">
          <button
            onClick={onOpenMenu}
            className="w-full flex flex-col items-center justify-center gap-0.5 py-2 px-1 min-h-[60px] text-[11px] font-medium text-gray-400 hover:text-gray-200 active:text-yellow-300 transition-colors"
            aria-label="Ouvrir le menu complet"
          >
            <Menu size={22} />
            <span className="leading-none">Plus</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
